import React, { useState } from 'react';
import {
  useForm,
  Controller,
  Resolver,
  FieldError,
  FieldErrorsImpl,
  Merge
} from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { Grid, Button, Typography, Box, Modal } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import CustomFileUploader from './CustomFileUploader';
import {
  gray100,
  gray90,
  primaryButtonBackgroundColor,
  red10,
  red100
} from '../../constants/Colors';
import { yupResolver } from '@hookform/resolvers/yup';
import { documentSchema } from '../../validation/co2Setting';
import { DocumentDataType } from '../../validation/products/productType';
import { getCo2Setting } from '../../api/db/co2Setting';
import { Co2SettingValueType } from '../../validation/co2Setting/co2SettingType';

interface Props {
  setModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  modalOpen: boolean;
}

interface documentType {
  document?: DocumentDataType | undefined;
}

// CSVの読み込み処理
function mapCSVToArray(csv: string): string[][] {
  return csv.split(/\r?\n/).map((row) => row.split(','));
}

const CSVImportModal = (props: Props) => {
  const {
    control,
    handleSubmit,
    getValues,
    reset,
    formState: { errors }
  } = useForm<documentType>({
    defaultValues: {
      document: undefined
    },
    mode: 'onChange',
    resolver: yupResolver(documentSchema) as Resolver<documentType>
  });

  const [errorText, setErrorText] = useState<string[]>(['']);
  const [open, setOpen] = useState<boolean>(false);

  const { setModalOpen, modalOpen } = props;
  const navigate = useNavigate();

  const handleCSVImport = async () => {
    const file = getValues('document');

    if (file && file.binary) {
      const base64String = file.binary.split(',')[1];

      // base64 文字列をバイナリにデコード
      const binaryString = atob(base64String);

      // バイナリデータを Uint8Array に変換
      const bytes = new Uint8Array(binaryString.length);
      for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }

      // TextDecoder を使用して文字列に変換
      const decoder = new TextDecoder('shift-jis');
      const csvString = decoder.decode(bytes);

      const csvData = mapCSVToArray(csvString); // CSV文字列を配列に変換

      globalCsvData = csvData;
    }
  };

  // バリデーション関数
  const validateMaterialIds = async (csvData: string[][]) => {
    const errors: string[] = [];

    try {
      // APIから取得したデータを取得
      const fetchedData = await getCo2Setting();
      const co2SettingValuesArray: Co2SettingValueType[] = Object.values(
        fetchedData.co2_setting_values
      );

      if (!fetchedData || !fetchedData.co2_setting_values) {
        errors.push('CO2設定データの取得に失敗しました');
        return errors;
      }

      // APIのデータ（fetchedData.co2_setting_values）とCSVデータを比較
      co2SettingValuesArray.forEach((apiItem) => {
        const matchingCsvRow = csvData.find(
          (csvRow) => Number(csvRow[1]) === apiItem.material_id
        );

        if (!matchingCsvRow) {
          errors.push(
            `部材ID(${apiItem.material_id})：${apiItem.material_name}が足りません`
          );
        }
      });
    } catch (error) {
      errors.push('データの検証中にエラーが発生しました');
    }

    return errors;
  };

  const validateCSVColumns = (globalCsvData: string[][]) => {
    const errors: string[] = [];

    // ヘッダー行を取得
    const headers = globalCsvData[0];

    // 各列のインデックス
    const requiredColumns = [
      { index: 0, name: '大分類', number: 'A' },
      { index: 1, name: '部材ID', number: 'B' },
      { index: 2, name: '部材名', number: 'C' },
      { index: 3, name: 'CO2基準値', number: 'D' },
      { index: 4, name: '単位', number: 'E' },
      { index: 5, name: '基準CO2算定仕様', number: 'F' },
      { index: 6, name: '備考', number: 'G' }
    ];

    // 各列のチェック
    requiredColumns.forEach((column) => {
      if (!headers[column.index]) {
        errors.push(`・${column.number}列に${column.name}が必要です`);
      }
    });

    return errors; // エラーメッセージの配列を返す
  };

  const validateCO2Value = (value: string): string | null => {
    const numValue = parseFloat(value);

    if (isNaN(numValue)) {
      return '数値を入力してください';
    }

    if (numValue >= 100) {
      if (!Number.isInteger(numValue)) {
        return '100以上の値は整数で入力してください';
      }
    } else {
      if (!/^\d+(\.\d{1,3})?$/.test(value)) {
        return '99以下の値は小数第3位までで入力してください';
      }
    }

    return null;
  };

  const validateRemarks = (value: string): string | null => {
    if (value.length > 250) {
      return '250文字以内で入力してください';
    }

    return null;
  };

  const validateValues = (csvData: string[][]) => {
    const errors: string[] = [];
    csvData.forEach((row) => {
      const co2Error = validateCO2Value(row[3]);
      const remarksError = validateRemarks(row[6]);
      if (co2Error) {
        errors.push(`部材ID(${row[1]})：${row[2]}のCO2基準値は、${co2Error}`);
      }
      if (remarksError) {
        errors.push(`部材ID(${row[1]})：${row[2]}の備考は、${remarksError}`);
      }
    });
    return errors;
  };

  const getDocumentErrorMessage = (
    errors:
      | Merge<
          FieldError,
          FieldErrorsImpl<{
            name: string;
            binary: string;
            kind: string;
            id: number;
            size: number;
          }>
        >
      | undefined
  ) => {
    let errorMessage = '';

    if (errors) {
      if (errors && errors.name && errors.name.message) {
        errorMessage = errors.name.message;
        return errorMessage;
      }
      if (errors && errors.binary && errors.binary.message) {
        errorMessage = errors.binary.message;
        return errorMessage;
      }
    }

    return errorMessage;
  };

  let globalCsvData: string[][] = [];
  const onSubmit = async () => {
    await handleCSVImport();
    const csvData = globalCsvData.slice(1);

    let allErrors: string[] = [];

    // カラムバリデーションの実行
    const columnErrors = validateCSVColumns(globalCsvData);
    allErrors = allErrors.concat(columnErrors);

    // 部材IDのバリデーション
    const idErrors = await validateMaterialIds(csvData);
    allErrors = allErrors.concat(idErrors);

    // 値のバリデーションの実行
    const valueErrors = validateValues(csvData);
    allErrors = allErrors.concat(valueErrors);

    if (allErrors.length > 0) {
      setErrorText(allErrors);
      setOpen(true); // スナックバーを表示
      return; // エラーがあるため処理を停止
    }

    navigate('/standard/csv_confirmation', {
      state: { csvData: csvData }
    });
  };

  const resetError = () => {
    setErrorText(['']);
    setOpen(false);
  };

  return (
    <>
      <Modal
        open={modalOpen}
        onClose={() => {
          setModalOpen(false);
          reset();
          setErrorText(['']);
          setOpen(false);
        }}
      >
        <Box
          sx={{
            width: '516px',
            borderSizing: 'border-box',
            bgcolor: '#FFF',
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            padding: '32px',
            '@media screen and (max-width:600px)': {
              width: 'calc(100% - 24px - 24px - 32px)',
              padding: '24px',
              margin: '40px 16px 0',
              top: '0',
              left: '0',
              transform: 'none',
              overflowY: 'auto',
              maxHeight: '80%'
            }
          }}
        >
          <Grid
            container
            flexDirection={'row'}
            justifyContent={'space-between'}
            marginBottom="24px"
          >
            <Typography sx={{ color: gray100, fontSize: '24px' }}>
              CSV取込
            </Typography>
            <CloseIcon
              sx={{ color: gray90, width: '24px', cursor: 'pointer' }}
              onClick={() => {
                setModalOpen(false);
                reset();
                setErrorText(['']);
                setOpen(false);
              }}
            />
          </Grid>
          <Typography sx={{ color: gray100, fontSize: '16px', mb: '24px' }}>
            CSV出力したCO2基準値を編集して、ファイルを選択してください。
          </Typography>
          <Controller
            name="document"
            control={control}
            render={({ field }) => (
              <CustomFileUploader
                formMb="24px"
                value={field.value as DocumentDataType | null}
                onChange={field.onChange}
                error={Boolean(errors.document)}
                helperText={getDocumentErrorMessage(errors.document)}
                fileType="document"
                resetError={resetError}
              />
            )}
          />
          {open && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                mt: '12px',
                backgroundColor: red10,
                color: gray100,
                borderRadius: '4px',
                width: '100%',
                border: '1px solid red',
                fontSize: '14px'
              }}
            >
              <ErrorOutlineIcon
                sx={{
                  color: red100,
                  width: '22px',
                  height: '22px',
                  margin: '16px'
                }}
              />
              <Grid container flexDirection={'column'} padding={'16px'}>
                {errorText.map((text, index) => (
                  <Grid
                    container
                    flexDirection={'row'}
                    flexWrap={'nowrap'}
                    key={index}
                  >
                    <Typography>・</Typography>
                    <Typography
                      sx={{
                        whiteSpace: 'pre-wrap'
                      }}
                    >
                      {text}
                    </Typography>
                  </Grid>
                ))}
              </Grid>
            </Box>
          )}
          <Box sx={{ mt: '40px' }}>
            <Button
              disabled={Boolean(errors.document)}
              sx={{ ...primaryButtonBackgroundColor, width: '100%' }}
              onClick={handleSubmit(onSubmit)}
            >
              CSVを取り込む
            </Button>
          </Box>
        </Box>
      </Modal>
    </>
  );
};

export default CSVImportModal;
